import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { useToast } from '@/components/common/toast';
import { lpMarginThresholdApi } from '@/api/services/lpMarginThreshold';
import type { LpMarginThresholdProfileDto, LpMarginThresholdSaveDto } from '@/api/types/lpMarginThreshold';

interface Grants {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

interface FormState {
  id: number | null;
  name: string;
  platformIds: string[];
  v1: string;
  v2: string;
  v3: string;
  sendEmail: boolean;
}
const empty: FormState = { id: null, name: '', platformIds: [], v1: '', v2: '', v3: '', sendEmail: false };

export function MarginThresholdPanel({ open, onClose, grants }: { open: boolean; onClose: () => void; grants: Grants }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(empty);

  const lpAccounts = useQuery({ queryKey: ['lmt', 'lp-accounts'], queryFn: lpMarginThresholdApi.getLpAccounts, staleTime: 5 * 60_000, enabled: open });
  const list = useQuery({ queryKey: ['lmt', 'list'], queryFn: lpMarginThresholdApi.getList, enabled: open });

  const reset = () => setForm(empty);
  const fill = (p: LpMarginThresholdProfileDto) =>
    setForm({
      id: p.id,
      name: p.name ?? '',
      platformIds: (p.platformIds ?? []).map(String),
      v1: p.threshold1 != null ? String(p.threshold1) : '',
      v2: p.threshold2 != null ? String(p.threshold2) : '',
      v3: p.threshold3 != null ? String(p.threshold3) : '',
      sendEmail: !!p.sendEmailNotifications,
    });

  const num = (v: string): number | null => {
    if (v.trim() === '') return null;
    const n = parseFloat(v.replace(/,/g, ''));
    return Number.isNaN(n) ? null : n;
  };

  const save = async () => {
    const dto: LpMarginThresholdSaveDto = {
      id: form.id,
      name: form.name.trim(),
      platformIds: form.platformIds.map((v) => parseInt(v, 10)).filter(Number.isFinite),
      threshold1: num(form.v1),
      threshold2: num(form.v2),
      threshold3: num(form.v3),
      sendEmailNotifications: form.sendEmail,
    };
    if (!dto.name) return toast.error('Please enter a profile name.');
    if (!dto.platformIds.length) return toast.error('Please select at least one LP account.');
    const vals = [dto.threshold1, dto.threshold2, dto.threshold3].filter((v): v is number => v != null);
    if (!vals.length) return toast.error('Please enter at least one threshold value.');
    // Margin alerts must be strictly DECREASING (e.g. 200 > 150 > 100).
    for (let i = 1; i < vals.length; i++) {
      if (vals[i] >= vals[i - 1]) return toast.error('Threshold values must be in strictly decreasing order.');
    }
    try {
      await lpMarginThresholdApi.save(dto);
      toast.success(form.id ? 'Profile saved successfully' : 'Profile created successfully');
      reset();
      qc.invalidateQueries({ queryKey: ['lmt', 'list'] });
    } catch {
      toast.error('Failed to save profile');
    }
  };

  const refetch = () => qc.invalidateQueries({ queryKey: ['lmt', 'list'] });
  const toggle = async (p: LpMarginThresholdProfileDto, next: boolean) => {
    try {
      await lpMarginThresholdApi.toggle(p.id, next);
      toast.success(next ? 'Profile enabled successfully' : 'Profile disabled successfully');
      refetch();
    } catch {
      toast.error('Failed to toggle profile');
    }
  };
  const remove = async (p: LpMarginThresholdProfileDto) => {
    if (!window.confirm('This will permanently delete this alert profile. Are you sure?')) return;
    try {
      await lpMarginThresholdApi.remove(p.id);
      toast.success('Profile deleted successfully');
      refetch();
    } catch {
      toast.error('Failed to delete profile');
    }
  };

  return (
    <>
      <div className={`offcanvas offcanvas-end${open ? ' show' : ''}`} tabIndex={-1} style={{ visibility: open ? 'visible' : 'hidden' }}>
        <div className="offcanvas-header border-bottom py-2 px-3">
          <h5 className="offcanvas-title fw-bold mb-0">Margin Level Thresholds</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>
        <div className="offcanvas-body">
          <div className="card mb-0 bg-light">
            <div className="card-body p-3 fs-12 text-muted">
              A notification will be triggered whenever the margin level of any selected LP account falls below any of the configured thresholds.
            </div>
          </div>

          <div className="pt-4 pb-2">
            <h6 className="fw-bold mb-3 fs-15">{form.id ? 'Edit' : 'Create'} Alert Profile</h6>
            <div className="mb-2">
              <label className="form-label">Profile Name</label>
              <input className="form-control" placeholder="Enter profile name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="mb-2">
              <Select2Multi label="LP Accounts" placeholder="Select LP accounts" searchable value={form.platformIds} options={lpAccounts.data ?? []} onChange={(v) => setForm((f) => ({ ...f, platformIds: v }))} />
            </div>
            <div className="row g-2 mb-3">
              {(['v1', 'v2', 'v3'] as const).map((k, i) => (
                <div className="col-4" key={k}>
                  <label className="form-label">Alert {i + 1} (%)</label>
                  <input className="form-control" inputMode="decimal" value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div className="form-check mb-2">
              <input className="form-check-input" type="checkbox" id="marginSendEmail" checked={form.sendEmail} onChange={(e) => setForm((f) => ({ ...f, sendEmail: e.target.checked }))} />
              <label className="form-check-label fs-14" htmlFor="marginSendEmail">Send email notifications</label>
            </div>
            <div className="d-flex justify-content-end gap-2 mb-1 mt-4">
              {form.id != null && <button type="button" className="btn btn-dark px-3" onClick={reset}>Cancel</button>}
              {((form.id == null && grants.canCreate) || (form.id != null && grants.canEdit)) && (
                <button type="button" className="btn btn-primary px-4" onClick={save}>{form.id ? 'Save' : 'Create'}</button>
              )}
            </div>
          </div>

          <div className="pt-4">
            <h6 className="fw-bold mb-2 fs-15">Alert Profiles</h6>
            <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
              <table className="table table-sm table-hover mb-0">
                <tbody>
                  {(list.data ?? []).length === 0 ? (
                    <tr><td className="text-center text-muted py-3">No alert profiles</td></tr>
                  ) : (
                    (list.data ?? []).map((p) => (
                      <tr key={p.id}>
                        <td>
                          <div role={grants.canEdit ? 'button' : undefined} onClick={() => grants.canEdit && fill(p)}>
                            <div className="fw-semibold fs-14">{p.name || `Profile #${p.id}`}</div>
                            <div className="text-muted fs-10 text-truncate" title={p.summary ?? ''}>
                              {(p.summary ?? '').length > 40 ? `${(p.summary ?? '').slice(0, 40).trimEnd()}…` : p.summary}
                            </div>
                          </div>
                        </td>
                        <td className="text-end" style={{ width: 90 }}>
                          <div className="form-check form-switch d-inline-block align-middle m-0 me-2">
                            <input type="checkbox" role="switch" className="form-check-input" checked={p.isActive} disabled={!grants.canEdit} onChange={(e) => toggle(p, e.target.checked)} />
                          </div>
                          {grants.canDelete && (
                            <button type="button" className="btn btn-xs btn-outline-danger" title="Delete" onClick={() => remove(p)}>
                              <i className="fa-solid fa-trash-can" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {open && <div className="offcanvas-backdrop fade show" onClick={onClose} />}
    </>
  );
}
